import { skipToken } from '@apollo/client/react'
import { useLocalStorage } from '@mantine/hooks'
import { pick } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { tv } from 'tailwind-variants'
import { joinURL, withQuery } from 'ufo'
import * as v from 'valibot'
import MdiBrush from '~icons/mdi/brush'
import { PromptsCompletionProvider } from 'src/features/Prompt/PromptsCompletion/PromptsCompletionProvider'
import { PromptsCompletionSwitch } from 'src/features/Prompt/PromptsCompletion/PromptsCompletionSwitch'
import { PromptsCompletionTextarea } from 'src/features/Prompt/PromptsCompletion/PromptsCompletionTextarea'
import { useTagComplete } from 'src/features/Prompt/store/autoComplete'
import type { UseTypewriterTextOptions } from 'src/features/Prompt/utils/useTypewriterText'
import { useTypewriterText } from 'src/features/Prompt/utils/useTypewriterText'
import { WORKBENCH_OVERLAY_ID } from 'src/layouts/WorkbenchLayout'
import { useMyInfo } from 'src/services/apollo/auth'
import { useGetGenerationModelByVersionId } from 'src/services/apollo/generation-model'
import { useDynamicConstants } from 'src/services/constants'
import { cx } from 'src/utils/cx'
import { getDefaultParamsFromModelVersionExtra } from 'src/utils/generationModel/modelParams'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { formatQs } from 'src/utils/qs'
import { endEventGroup, startEventGroup, track } from 'src/utils/tracker'
import { UIButton } from '../ui/Button'
import BaseTaskSubmitForm, {
  useTaskSubmitFormContext,
} from './BaseTaskSubmitForm'
import { defaultTaskFormValues, SIDEBAR_SIZES } from './constants'
import {
  encodeInitialValuesForImport,
  encodeTaskForImport,
} from './MultiColumnGenerationForm/importEncode'
import { useObserveSimpleGenIntersection } from './SimpleGenerationForm.store'

const simpleGenFormStyles = tv({
  slots: {
    container: 'mx-auto w-full max-w-3xl rounded-2xl p-2',
    textContainer: 'relative flex-1',
    textarea:
      'w-full mx-auto py-3 bg-transparent transition-all outline-none resize-none',
    ghostText:
      'pointer-events-none absolute inset-0 z-10 select-none py-3 leading-6',
    buttonWrapper: '',
  },
  variants: {
    variant: {
      treatment: {
        container:
          'flex bg-white dark:bg-neutral-900 border border-[#E4DBFA] dark:border-neutral-700/60 shadow-[0_2px_8px_0_rgba(0,0,0,0.06)]',
        textarea: 'placeholder:text-[#9C7CEF]',
        ghostText: 'whitespace-nowrap text-[#9C7CEF]',
        buttonWrapper: 'flex justify-end',
      },
      control: {
        container:
          'flex justify-center gap-1.5 mlg:rounded-3xl items-start bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border border-neutral-200/60 dark:border-neutral-700/60 shadow-lg shadow-neutral-200/50 dark:shadow-neutral-950/50 hover:shadow-xl hover:shadow-neutral-300/60 dark:hover:shadow-neutral-900/60 hover:border-neutral-300/80 dark:hover:border-neutral-600/80',
        textContainer: 'overflow-hidden',
        textarea: 'placeholder:truncate',
        ghostText: 'px-0 truncate text-gray-400 dark:text-gray-500',
        buttonWrapper: 'flex flex-col mt-auto flex-shrink-0',
      },
    },
  },
  defaultVariants: {
    variant: 'control',
  },
})

const SIMPLE_GENERATION_FORM_INPUT_KEY = 'sgfiv'
const SIMPLE_GENERATION_FORM_INPUT_UPDATED_AT_KEY = 'sgfiv::updated-at'
const STALE_PROMPT_TTL_MS = 7 * 24 * 60 * 60 * 1000

function TypewriterGhostText(props: {
  enabled: boolean
  target: string | string[]
  className?: string
  options?: Omit<UseTypewriterTextOptions, 'enabled' | 'stopWhen'>
}) {
  const text = useTypewriterText(props.target, {
    enabled: props.enabled,
    ...props.options,
  })

  if (!props.enabled) return null

  return (
    <div aria-hidden="true" className={props.className}>
      {text}
    </div>
  )
}

/** Safeguards for typewriter targets i18n (control group) */
const FALLBACK_TYPEWRITER_TARGETS = [
  'a girl reading under cherry blossoms, sunlight filtering through petals',
  'neon-lit Tokyo alley at night, rain reflections, cinematic',
  'a cat napping on a stack of old books, soft afternoon light',
]

const StringEntriesSchema = v.record(v.string(), v.string())
const StringArraySchema = v.array(v.string())
function ensureTypewriterTargets(obj: unknown): string[] {
  if (v.is(StringEntriesSchema, obj)) {
    return Object.values(obj)
  }
  if (v.is(StringArraySchema, obj)) {
    return obj
  }
  return FALLBACK_TYPEWRITER_TARGETS
}

function SimpleGenerationFormContent({
  isTreatment,
}: {
  isTreatment: boolean
}) {
  const { t } = useTranslation(['artwork', 'generation'])
  const typewriterTargetsRaw = t('artwork:generation.typewriter-targets', {
    returnObjects: true,
  })
  const typewriterTargets = useMemo(() => {
    return ensureTypewriterTargets(typewriterTargetsRaw)
  }, [typewriterTargetsRaw])

  const navigate = useNavigate()
  const { loading, submit } = useTaskSubmitFormContext()
  const { data: userInfo } = useMyInfo()

  const { data, isLoading: constantsLoading } = useDynamicConstants({
    revalidateOnFocus: false,
  })
  const firstPresetModelId = data?.simpleGenerationModelIds?.[0]
  const presetModelId = firstPresetModelId

  const { data: presetModelVersion, loading: modelLoading } =
    useGetGenerationModelByVersionId(presetModelId || skipToken, {
      context: {
        revalidateOnFocus: false,
      },
    })

  const modelType = presetModelVersion?.model?.type
  const defaultSize = SIDEBAR_SIZES.find(
    it => it.extraProps?.modelType === modelType,
  )

  const [inputValue, setInputValue] = useLocalStorage({
    key: SIMPLE_GENERATION_FORM_INPUT_KEY, // simple generation form input value
    defaultValue: '',
  })
  const [inputLastUpdatedAt, setInputLastUpdatedAt, removeInputLastUpdatedAt] =
    useLocalStorage<number | null>({
      key: SIMPLE_GENERATION_FORM_INPUT_UPDATED_AT_KEY,
      defaultValue: null,
    })

  useEffect(() => {
    if (!inputValue) {
      if (inputLastUpdatedAt !== null) removeInputLastUpdatedAt()
      return
    }

    if (inputLastUpdatedAt === null) {
      setInputLastUpdatedAt(Date.now())
      return
    }

    const lastUpdatedAt =
      typeof inputLastUpdatedAt === 'number' ? inputLastUpdatedAt : 0
    const isStale = Date.now() - lastUpdatedAt >= STALE_PROMPT_TTL_MS

    if (isStale) {
      setInputValue('')
      removeInputLastUpdatedAt()
    }
  }, [
    inputLastUpdatedAt,
    inputValue,
    setInputValue,
    setInputLastUpdatedAt,
    removeInputLastUpdatedAt,
  ])

  const isLoggedIn = !!userInfo
  // Treatment: logged-out always expanded & non-collapsible; logged-in starts
  //            collapsed and behaves like control (expands on focus).
  // Control: collapsed by default unless they have input.
  const [internalExpanded, setExpanded] = useState(
    isTreatment ? !isLoggedIn : inputValue.length > 0,
  )

  const showTypewriter = inputValue === ''

  // JSX variable (not component) keeps element type stable, preventing react-aria press listeners from detaching on re-render.
  const generateButton = (
    <UIButton
      color="theme"
      variant="filled"
      className={cx(
        `w-auto h-12 px-7 mlg:px-[30px] text-white disabled:bg-opacity-40 rounded-2xl text-base ${isTreatment ? 'font-bold' : 'font-semibold'}`,
        'bg-gradient-to-r from-violet-600 to-purple-600',
        'hover:from-violet-500 hover:to-purple-500 hover:shadow-lg hover:shadow-violet-500/25',
        'dark:from-violet-600 dark:to-purple-600 dark:hover:from-violet-500 dark:hover:to-purple-500',
        'active:scale-[0.98]',
        'transition-all duration-300 ease-in-out',
        'mt-auto',
      )}
      onFocus={e => e.stopPropagation()}
      onPress={() => {
        track('start_creating_click')

        const initialValues = {
          modelId: presetModelId,
          ...defaultSize,
          ...pick(defaultTaskFormValues, [
            'batchSize',
            'negativePrompts',
            'samplingSteps',
            'samplingMethod',
            'cfgScale',
          ]),
          ...getDefaultParamsFromModelVersionExtra(
            {
              versionExtra: presetModelVersion?.extra,
              modelId: presetModelVersion?.model?.id,
              modelType: presetModelVersion?.model?.type,
            },
            {
              enableLightningMode: true,
            },
          ),
          isHomepage: true,
          prompts: inputValue,
        }

        startEventGroup('generate_source')
        if (inputValue === '') {
          const generatorQuery = {
            initialValues: encodeInitialValuesForImport(initialValues),
          }
          return navigate(
            userInfo
              ? withQuery('/generator', generatorQuery)
              : withQuery('/generator', {
                  ...generatorQuery,
                  showSignUpModal: 1,
                  signupModalRedirect: withQuery(
                    joinURL(window.location.origin, 'generator'),
                    generatorQuery,
                  ),
                }),
          )
        }

        // User has input values, submit task on auth complete
        if (!userInfo) {
          return navigate(
            withQuery('/generator', {
              showSignUpModal: 1,
              initialValues: encodeInitialValuesForImport(initialValues),
              submitTask: 1,
              // Refill initial values after oauth redirection
              signupModalRedirect: withQuery(
                `${joinURL(window.location.origin, 'generator')}`,
                {
                  initialValues: encodeInitialValuesForImport(initialValues),
                  submitTask: 1,
                },
              ),
            }),
          )
        }

        try {
          submit(
            async () => {
              return initialValues
            },
            res => {
              navigate(
                `/generator/image?${formatQs({
                  task: encodeTaskForImport(res.createGenerationTask),
                  showOnStage: '1',
                })}`,
              )
            },
            { hideSubmissionToast: true },
          )
        } catch {
          endEventGroup('generate_source')
        }
      }}
      disabled={inputValue !== '' && (constantsLoading || modelLoading)}
      busy={loading}
    >
      <span className="relative">{t('common:home.start-creating.label')}</span>
    </UIButton>
  )

  const elRef = useObserveSimpleGenIntersection()
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Callback ref: merges two refs without recreating on each render.
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node
      elRef(node)
    },
    [elRef],
  )

  // Collapse on outside click or window blur.
  // Treatment: only logged-in users can collapse. Control: all users.
  const canCollapse = isTreatment ? isLoggedIn : true
  // When collapse is disabled (e.g. user logs out from treatment), the stored
  // expanded state may still be `false` — treat it as expanded so the layout
  // doesn't render with collapsed dimensions but uncollapsible (no overflow-hidden).
  const expanded = canCollapse ? internalExpanded : true
  useEffect(() => {
    if (!canCollapse || !expanded || inputValue !== '') return
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setExpanded(false)
    }
    const onWindowBlur = () => setExpanded(false)
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('blur', onWindowBlur)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('blur', onWindowBlur)
    }
  }, [canCollapse, expanded, inputValue])

  const shouldDisableCompletion = useBreakpoint('mlg')
  const [enableCompletion] = useTagComplete()

  const tvVariant = isTreatment ? 'treatment' : 'control'
  const styles = simpleGenFormStyles({ variant: tvVariant })

  const textContent = (
    <div className={styles.textContainer()}>
      <PromptsCompletionTextarea
        id="simple-generation-form"
        rows={!expanded && canCollapse ? 1 : 4}
        value={inputValue}
        placeholder={
          showTypewriter
            ? ''
            : t(
                isTreatment
                  ? 'artwork:generation.home-placeholder'
                  : 'artwork:generation.placeholder',
              )
        }
        onChange={e => {
          const value = e.target.value
          setInputValue(value)
          if (value) {
            setInputLastUpdatedAt(Date.now())
          } else {
            removeInputLastUpdatedAt()
          }
        }}
        className={cx(
          styles.textarea(),
          isTreatment && !expanded && 'placeholder:truncate',
        )}
      />
      <TypewriterGhostText
        enabled={showTypewriter}
        className={cx(
          styles.ghostText(),
          isTreatment && !expanded && 'truncate',
        )}
        target={
          isTreatment
            ? t('artwork:generation.home-placeholder')
            : typewriterTargets
        }
        options={{
          delayMs: 0,
          charIntervalMs: 36,
          loop: true,
          backspaceIntervalMs: 24,
          pauseAfterTypeMs: 2000,
          pauseAfterDeleteMs: 1000,
        }}
      />
    </div>
  )

  return (
    <PromptsCompletionProvider
      enable={enableCompletion && !shouldDisableCompletion}
    >
      {!shouldDisableCompletion && (isTreatment || expanded) && (
        <>
          <div
            // prompts completion display will be rendered in a portal to this element
            id={WORKBENCH_OVERLAY_ID}
            // The header bar has z-index 30, so we need to make sure the overlays are above it
            className="fixed top-0 left-0 z-40 w-full h-0"
          />
        </>
      )}
      <div
        ref={setContainerRef}
        className={cx(
          styles.container(),
          // Treatment expanded: column. Treatment collapsed: row (button stays inline).
          isTreatment && expanded && 'flex-col',
          canCollapse && 'transition-all duration-300 ease-out overflow-hidden',
          canCollapse && (expanded ? (isTreatment ? 'h-52' : 'h-40') : 'h-16'),
        )}
        onFocus={() => canCollapse && setExpanded(true)}
        onBlur={e => {
          if (!canCollapse) return
          if (e.currentTarget.contains(e.relatedTarget as Node)) return
          if (e.relatedTarget === null) return
          if (inputValue === '') setExpanded(false)
        }}
      >
        {!isTreatment && (
          <div className="flex items-center justify-center px-2 h-12">
            <MdiBrush className="text-primary opacity-50 size-6" />
          </div>
        )}

        {isTreatment ? (
          <div
            className={cx(
              'flex flex-1 gap-2 px-2',
              expanded ? 'items-start' : 'items-center h-12',
            )}
          >
            <MdiBrush
              className={cx(
                'text-[#9C7CEF] size-6 shrink-0',
                expanded && 'mt-3',
              )}
            />
            {textContent}
          </div>
        ) : (
          textContent
        )}

        <div className={styles.buttonWrapper()}>
          {!isTreatment && !shouldDisableCompletion && (
            <PromptsCompletionSwitch
              labelPlacement="end"
              className={cx(
                'relative mx-auto transition-all duration-300',
                expanded
                  ? '-top-2 opacity-100 max-h-4'
                  : 'top-0 opacity-0 max-h-0',
                !expanded && 'pointer-events-none',
              )}
            />
          )}
          {generateButton}
        </div>
      </div>
    </PromptsCompletionProvider>
  )
}

export default function SimpleGenerationForm({
  isInputboxRefreshTreatment = false,
}: {
  isInputboxRefreshTreatment?: boolean
}) {
  return (
    <BaseTaskSubmitForm>
      <SimpleGenerationFormContent isTreatment={isInputboxRefreshTreatment} />
    </BaseTaskSubmitForm>
  )
}
